import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useCategories } from '@/hooks/useTools';
import { useCategoryCounts } from '@/hooks/useCategoryCounts';
import { Skeleton } from '@/components/ui/skeleton';
import { trackHome } from '@/lib/homeAnalytics';
import { ToolLogo, PRICING_LABEL } from './ToolMark';

export function RecentlyAdded({ excludeIds }: { excludeIds: string[] }) {
  const { data = [], isLoading } = useQuery({
    queryKey: ['home-recent-tools'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tools')
        .select('id, name, slug, short_description, logo_url, pricing, created_at')
        .eq('status', 'approved')
        .order('created_at', { ascending: false })
        .limit(16);
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const tools = data.filter((t) => !excludeIds.includes(t.id)).slice(0, 6);
  if (!isLoading && tools.length === 0) return null;

  return (
    <section className="border-y border-border/60 bg-muted/20">
      <div className="container mx-auto px-4 py-14 md:py-16">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary mb-2">Recently added</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">New in the directory.</h2>
            </div>
            <Link to="/tools" className="hidden sm:inline-flex items-center text-sm font-medium text-primary hover:underline">
              See all <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8">
            {isLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <li key={i} className="py-4 border-b border-border/60"><Skeleton className="h-12 w-full" /></li>
                ))
              : tools.map((t) => (
                  <li key={t.id} className="border-b border-border/60 min-w-0">
                    <Link
                      to={`/tool/${t.slug}`}
                      onClick={() => trackHome('featured_tool_opened', `recent:${t.slug}`)}
                      className="group flex items-center gap-4 py-4 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <ToolLogo tool={t} className="h-10 w-10" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <h3 className="font-semibold truncate group-hover:text-primary transition-colors">{t.name}</h3>
                          {PRICING_LABEL[t.pricing] && (
                            <span className="text-xs text-muted-foreground shrink-0">{PRICING_LABEL[t.pricing]}</span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{t.short_description}</p>
                      </div>
                    </Link>
                  </li>
                ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

export function ExploreCategories() {
  const { data: categories = [] } = useCategories();
  const { data: counts = {} } = useCategoryCounts();
  const top = [...categories]
    .filter((c) => (counts[c.id] ?? counts[c.slug] ?? 0) > 0)
    .sort((a, b) => (counts[b.id] ?? counts[b.slug] ?? 0) - (counts[a.id] ?? counts[a.slug] ?? 0))
    .slice(0, 12);
  if (top.length === 0) return null;

  return (
    <section className="container mx-auto px-4 py-14 md:py-16">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary mb-2">Explore by category</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Browse the directory.</h2>
          </div>
          <Link to="/categories" className="hidden sm:inline-flex items-center text-sm font-medium text-primary hover:underline">
            All categories <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
        <div className="flex flex-wrap gap-2">
          {top.map((c) => (
            <Link
              key={c.id}
              to={`/categories/${c.slug}`}
              onClick={() => trackHome('goal_selected', `category:${c.slug}`)}
              className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full border border-border/60 bg-card hover:border-primary/50 hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <span className="font-medium">{c.name}</span>
              <span className="text-xs text-muted-foreground">{counts[c.id] ?? counts[c.slug]}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
